/**
 * El stock como lo cuentan las grandes (20261021111500): en existencia, comprometido y disponible.
 *
 *   en existencia  lo que está en el estante (incluye lo armado que todavía no salió)
 *   comprometido   lo vendido que todavía no salió
 *   disponible     lo que se puede vender: en existencia − comprometido
 *
 * DE DÓNDE SALE. El founder, 20-oct, con la grabación de Aliclik («Stock 15 / Virtual 14»): «stock físico y stock
 * disponible sí sería bueno tenerlo». El libro descuenta una venta cuando nace su envío, antes de que la caja salga:
 * lo que antes se mostraba como stock era el disponible, no el estante.
 *
 * ⭐ CONTAR ES DECIR CUÁNTO HAY EN EL ESTANTE: el servidor asienta la diferencia con lo que hay en existencia.
 */
import { supabase } from '@/lib/supabase';

export interface Existencia {
  enExistencia: number;
  comprometido: number;
  disponible: number;
}

export interface ExistenciaEnLugar extends Existencia {
  lugarId: string;
  nombre: string;
  /** La bodega del propio negocio (la única donde se cuenta desde acá). */
  enCasa: boolean;
}

type FilaDeExistencia = { fisico: number | null; comprometido: number | null; disponible: number | null };

const existenciaDe = (f: FilaDeExistencia): Existencia => ({
  enExistencia: Number(f.fisico ?? 0),
  comprometido: Number(f.comprometido ?? 0),
  disponible: Number(f.disponible ?? 0),
});

/** El total de cada producto del negocio, sumando todos sus lugares (la lista de Existencias). */
export async function existenciasDelNegocio(tenantId: string): Promise<Map<string, Existencia>> {
  const { data, error } = await supabase.rpc('fn_existencias_del_negocio', { p_tenant: tenantId });
  if (error) throw error;
  const filas = (data ?? []) as Array<FilaDeExistencia & { product_id: string }>;
  return new Map(filas.map((f) => [f.product_id, existenciaDe(f)]));
}

/** Cada lugar de un producto: la bodega propia primero y después cada almacén. */
export async function existenciasDelProducto(tenantId: string, productId: string): Promise<ExistenciaEnLugar[]> {
  const { data, error } = await supabase.rpc('fn_existencias_del_producto', { p_tenant: tenantId, p_producto: productId });
  if (error) throw error;
  const filas = (data ?? []) as Array<FilaDeExistencia & { lugar: string; nombre: string | null; en_casa: boolean }>;
  return filas.map((f) => ({ ...existenciaDe(f), lugarId: f.lugar, nombre: f.nombre ?? '—', enCasa: !!f.en_casa }));
}

/** Cuenta lo que hay en el estante de la bodega propia. Devuelve cuánto se ajustó (0 si ya coincidía). */
export async function contarExistencia(tenantId: string, productId: string, enExistencia: number, motivo?: string): Promise<number> {
  const { data, error } = await supabase.rpc('fn_existencia_contar', {
    p_tenant: tenantId, p_producto: productId, p_en_existencia: enExistencia, p_motivo: motivo?.trim() || null,
  });
  if (error) throw error;
  return Number(data ?? 0);
}
